import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {fontSize, wh} from '../../mixins';
import {useDispatch, useSelector} from 'react-redux';
import {selectLandingType, selectPlan, setDefaultPlanId, setPlan} from './plansSlice';
import {PinkBigCircle, WhiteBigCircle} from './icons';
import LatamOptions from './LatamOptions';
import LatamPlan from './LatamPlan';


const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({isPromo}) => isPromo ? '10px' : '0'} 10px 0;
`;

const StyledIcon = styled.span`
  position: absolute;
  top: 33%;
  right: 6%;
`;

const StyledList = styled.ul`
  width: 100%;
  max-width: 345px;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const StyledName = styled.span`
  ${fontSize(14)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  font-weight: 500;
`;

const StyledPrice = styled.span`
    ${fontSize(12)};
    line-height: 22px;
    letter-spacing: -0.225882px;
    color: #fff;
  
    &:first-letter {
        text-transform: uppercase;
    }
`;

const StyledLi = styled.li`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: ${({active}) => active ? 1 : 0}px;
  
    &:first-child {
        border-radius: 0 0 8px 8px;
        margin-top: 20px;
        
        &:before {
            content: attr(data-value);
            height: calc(100% + 20px);
            top: -20px;
        }
    }
    
    &:before {
        content: '';
        width: 100%;
        height: 100%;
        color: #fff;
        font-weight: 600;
        ${fontSize(10)};
        line-height: 1;
        text-transform: uppercase;
        background: linear-gradient(93.11deg, #D65C74 48.42%, #ECA5B3 100%);
        box-shadow: 0 4px 20px rgba(0,0,0,.25);
        border-radius: 8px;
        padding: 5px 16px;
        box-sizing: border-box;
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
    }
  
    button {
        width: 100%;
        height: 100%;
        color: #fff;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        background: linear-gradient(97.26deg, #454545 0%, #303030 100.55%);
        border-radius: inherit;
        border: none;
        padding: ${({active}) => active ? '11px 16px' : '12px 17px'};
        cursor: pointer;
        
        &:focus {
            outline: none;
        }
    }
`;

const StyledButtonWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: sticky;
    bottom: 24px;
    left: 0;
    
    ${({landingType,isSticked}) => (landingType !== 'optionsh') && `
        &:before {
            content: '';
            display: block;
            width: 100vw;
            height: 258px;
            position: absolute;
            left: calc(-50vw + 50%);
            top: -72px;
            background: linear-gradient(0deg,#b896b2 0%,#b896b2 64%,rgba(184,150,178,.35) 90%,rgba(184,150,178,0) 100%);
            pointer-events: none;
            transition: opacity .3s; 
            opacity: ${isSticked ? 1 : 0};
        }
    `}
`;

const InvisFlag = styled.div`
    ${wh('1px')};
    position: absolute;
    bottom: -25px;
    left: 0;
`;

const CustomButton = styled.div`
    ${wh('90%', 'auto')};
    max-width: 327px;
    background: #256EB0;
    color: #FFFFFF;
    ${fontSize(14)};
    line-height: 18px;
    font-weight: 500;
    padding: 14px 20px;
    border-radius: 24px;
    position: relative;
    text-align: center;
`;

const StyledOptionsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin: 24px 0 ${({isPromo}) => isPromo ? '40px' : '24px'};
`;

const StyledPromoPlan = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(93.11deg, #D65C74 48.42%, #ECA5B3 100%);
    box-shadow: 0 4px 20px rgba(0,0,0,.25);
    border-radius: 16px;
    padding: 12px 0 14px;
    color: #fff;
    font-weight: 800;
    ${fontSize(30)};
    line-height: 1;
    letter-spacing: -0.460567px;
    position: relative;
    
    &:before {
        content: '';
        display: block;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        position: absolute;
        top: -1px;
        left: -1px;
        background: linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,.4));
        border-radius: 17px;
        z-index: -1;
    }
    
    span:last-child {
        ${fontSize(16)};
        font-weight: normal;
        margin-top: 8px;
        
        &:first-letter {
            text-transform: uppercase;
        }
    }
`;

const StyledPromoTitle = styled.div`
    font-weight: 600;
    ${fontSize(24)};
    line-height: 100%;
    color: #fff;
    letter-spacing: -0.460567px;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: ${({isPromo}) => isPromo ? '24px' : '14px'};
`;


const ExperimentalPlans = ({onBuyClick}) => {
    const {t} = useTranslation();
    const plan = useSelector(selectPlan);
    const dispatch = useDispatch();
    const landingType = useSelector(selectLandingType);
    const isLatam = (landingType === 'latam');
    const isPromo = (landingType === 'promo');
    const [isSticked,setIsSticked] = useState(null);
    const flagRef = useRef(null);

    useEffect(() => {
        let ref = flagRef.current;
        const observer = new IntersectionObserver(
            ([e]) => setIsSticked(e.intersectionRatio < 1),
            { threshold: [1] }
        );
        if (ref) {
            window.scrollTo(0,0);
            observer.observe(ref);
        }

        return () => {
            if (ref) observer.unobserve(ref)
        }
    }, []);

    const PLANS = useMemo(() => {
        return {
            latam: [
                {
                    id: 'fl-monthly-999',
                    title: '1',
                    period: `${t('month')}`,
                    week: t('per_week'),
                    prevPrice: '$19.99',
                    weeklyPrice: '$2.49',
                    price: '$9.99',
                    mostPopular: true,
                    mostPopularDiscount: '-50%',
                },
                {
                    id: 'fl-3month-2399',
                    title: '3',
                    period: `${t('month')}`,
                    week: t('per_week'),
                    prevPrice: '$29.99',
                    weeklyPrice: '$1.99',
                    price: '$23.99',
                },
            ],
            promo: [
                {
                    id: 'fl-monthly-free3d-2999',
                    title: `3 ${t('3_days_free')}`,
                    price: `${t('then')} $7.49 ${t('per_week')}`,
                }
            ],
            trial: [
                {
                    id: 'fl-monthly-free3d-2999',
                    title: `3 ${t('3_days_free')}`,
                    price: `${t('then')} $7.49 ${t('per_week')}`,
                    bestseller: true,
                },
                {
                    id: 'fl-3month-3999',
                    title: `3 ${t('months')}`,
                    price: `$3.33 / ${t('week')}`,
                },
            ]
        }
    }, [t]);

    const onPlanClick = id => dispatch(setPlan(id));

    const currentPlans = useMemo(() => PLANS[landingType], [landingType, PLANS]);

    useEffect(() => {
        dispatch(setPlan(currentPlans[0].id));
        dispatch(setDefaultPlanId(currentPlans[0].id))
    }, [dispatch, currentPlans]);

    const renderOffer = (i, index) => {
        const isActive = plan === i.id;
        return <React.Fragment key={i.id}>
            <StyledLi active={isActive} data-value={t('most_popular')}>
                <button onClick={() => onPlanClick(i.id)} id={'plan-button-' + (index + 1)}>
                    <StyledName>{i.title.toUpperCase()}</StyledName>
                    <StyledPrice>{i.price}</StyledPrice>
                    <StyledIcon>
                        {isActive ? PinkBigCircle : WhiteBigCircle}
                    </StyledIcon>
                </button>
            </StyledLi>
        </React.Fragment>
    };

    const renderPromoPlan = (() => {
        const {title,price} = currentPlans[0];
        return <StyledPromoPlan>
            <span>{title}</span>
            <span>{price}</span>
        </StyledPromoPlan>;
    })();

    const buttonWrapper = <StyledButtonWrapper isSticked={isSticked} landingType={landingType}>
                            <CustomButton onClick={onBuyClick}>{t('continue')}</CustomButton>
                            <InvisFlag ref={flagRef}/>
                        </StyledButtonWrapper>;

    if (isLatam) {
        return <>
            <LatamPlan
                currentPlans={currentPlans}
                onPlanClick={onPlanClick}
                plan={plan}
            />
            {buttonWrapper}
        </>;
    }

    return <>
        <StyledContainer isPromo={isPromo}>
            <StyledPromoTitle isPromo={isPromo}>{t('unlock_all_features')}</StyledPromoTitle>
            {isPromo
                ? renderPromoPlan
                : <StyledList>
                    {currentPlans.map(renderOffer)}
                </StyledList>
            }
            <StyledOptionsWrapper isPromo={isPromo}>
                <LatamOptions isPromo={isPromo} />
            </StyledOptionsWrapper>
        </StyledContainer>
        {buttonWrapper}
    </>;
};

export default ExperimentalPlans;